import { useEffect, useRef, useState, type ReactNode } from 'react'

import EmptyState from '@/components/ui/EmptyState'
import { ArrivalCrossfade } from '@/components/ui/RefreshRegion'
import { stateCopy, type EmptyKind, type StateSize } from '@/components/ui/empty-state-variants'
import Skeleton, { SkeletonLayer } from '@/components/ui/Skeleton'
import { useSkeletonTiming } from '@/components/ui/use-skeleton-timing'
import { useRefreshLine } from '@/lib/motion/refresh'
import { cn } from '@/lib/utils'

/** `resolveViewState()` from `@/lib/view-state` returns all of these but `refreshing`. */
export type CardStateValue = 'loaded' | 'loading' | 'failed' | 'offline' | 'empty' | 'refreshing'

/**
 * Figma draws every card state at one fixed height, and this stack is how it
 * gets there: 12 + 16 + 20 + 12 + 12 + 16 + 48 = 136px against the loaded
 * body's 135 (the second term is 16, not the original 14, snapped to the
 * 4-grid). Matching it is the whole point — a skeleton shorter than the
 * content it stands in for moves the rest of the page when the data lands.
 *
 * It only holds for a card shaped like the index tile it was drawn from. A
 * caller whose loaded body is a 360px chart must pass its own `skeleton`.
 */
const DEFAULT_SKELETON = (
  <>
    <Skeleton className="h-3 w-30 rounded-xs" />
    <Skeleton className="mt-4 h-5 w-40 rounded-sm" />
    <Skeleton className="mt-3 h-3 w-28 rounded-xs" />
    <Skeleton className="mt-4 h-12 w-full rounded-md" />
  </>
)

interface CardStateProps {
  state: CardStateValue
  /** Renders "Try again" on `failed` and `offline`. Omit it and the message
   *  still shows, with no button rather than a dead one — the same contract
   *  as RefreshBar. */
  onRetry?: () => void
  /** The retry is in flight: the button reads "Trying…" and keeps focus. */
  retrying?: boolean
  /**
   * `offline`: the query's `dataUpdatedAt`, forwarded to EmptyState so the card carries the same
   * "Saved at …" line every offline state in the app does. 0 or omitted leaves it out.
   */
  savedAt?: number | undefined
  failedTitle?: string
  failedDescription?: string
  emptyTitle?: string
  emptyDescription?: string
  /** What kind of nothing `empty` is: `no-results` for a filter that matched nothing. */
  emptyKind?: EmptyKind
  /** `section` (default) for a card body; `inline` or `compact` where the body is a single short row. */
  size?: StateSize
  /** Replaces the default bar stack. Size it to YOUR loaded body; see above. */
  skeleton?: ReactNode
  className?: string
  /** The loaded body. Rendered on `loaded` and, as it stands until the new data lands, on
   *  `refreshing`. */
  children?: ReactNode
}

/**
 * The states a card body can be in, wrapped around the one body the caller
 * actually wrote. A screen branches once on `state` instead of spelling out a
 * skeleton, an error and an empty beside every panel.
 *
 * It paints no card of its own: the chrome comes from the SectionCard or Card
 * it sits inside, and a second border here would draw a card within a card.
 * `className` is merged last, so pass `min-h-*` when the loaded body is taller
 * than the skeleton and the placeholder should hold its space anyway.
 *
 * Failed, offline and empty are EmptyState, so a card failure reads the same
 * as every other failure in the app. Each copy prop keeps its own default.
 */
export default function CardState({
  state,
  onRetry,
  retrying,
  savedAt,
  failedTitle,
  failedDescription,
  emptyTitle,
  emptyDescription,
  emptyKind = 'empty',
  size = 'section',
  skeleton,
  className,
  children,
}: CardStateProps) {
  // The skeleton waits 80ms before it is drawn, and the content that replaces
  // a drawn one crosses it in the same box. See use-skeleton-timing.ts.
  const timing = useSkeletonTiming(state === 'loading')

  // A refresh leaves the body as it is, live and at full strength: a line on
  // the top edge past 300ms, and the new body crossing the old as it lands —
  // the same as any RefreshRegion. It used to dim the body to 45% under a
  // "Refreshing" pill.
  const isRefreshing = state === 'refreshing'
  const rootRef = useRef<HTMLDivElement>(null)
  useRefreshLine(rootRef, isRefreshing)

  // The status is filled in a render AFTER this mounts, not the one that
  // turns the skeleton on: a `role="status"` born with its words already
  // there gives a screen reader's mutation observer nothing to see, and it
  // only starts watching once it has attached. The effect below is the
  // fill, one render later — a real change to announce, the same reasoning
  // UpdatingNote gives for staying mounted through its own idle case rather
  // than arriving already spoken for.
  const [loadingAnnounced, setLoadingAnnounced] = useState(false)
  useEffect(() => {
    // Direct, not deferred to a microtask or moved to a `useLayoutEffect`:
    // a screen reader needs a GENUINE second paint, so the browser must
    // actually show the span empty before this fills it in. A layout
    // effect resolves before the browser paints anything, which collapses
    // both states into the one paint this exists to split apart. A
    // microtask-deferred update (the shape MitraPanel's `checkTokenOnOpen`
    // uses) is real in a browser but never settles inside a synchronous
    // test — confirmed against BrokerPerformanceCard's own "shows a
    // skeleton" test, which reads the status immediately after `render()`
    // with no `await`. A plain synchronous update inside a passive effect
    // is the one shape that is a true two-paint reveal in the browser and
    // still settles by the time `render()`'s `act()` flush returns in a
    // test.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    if (timing.showSkeleton) setLoadingAnnounced(true)
  }, [timing.showSkeleton])

  // Failed and empty replace the root outright — EmptyState is its own box,
  // sized by `className` — so no skeleton can stay to cross them; they fade
  // in over the space it left instead.
  if (state === 'failed' || state === 'offline') {
    const failed = stateCopy('failed')
    return (
      <EmptyState
        kind={state}
        size={size}
        // Offline has its own words; the failed ones would blame the wrong thing.
        title={state === 'failed' ? (failedTitle ?? failed.title) : undefined}
        description={state === 'failed' ? (failedDescription ?? failed.description) : undefined}
        onRetry={onRetry}
        retrying={retrying}
        savedAt={savedAt}
        className={cn(timing.revealClassName, className)}
      />
    )
  }

  if (state === 'empty') {
    const empty = stateCopy(emptyKind)
    return (
      <EmptyState
        kind={emptyKind}
        size={size}
        title={emptyTitle ?? empty.title}
        description={emptyDescription ?? empty.description}
        className={cn(timing.revealClassName, className)}
      />
    )
  }

  // One root for loading, the swap and the loaded body, and each slot in it
  // fixed, so React keeps the skeleton's own element from the first frame of
  // the load to the last frame of its fade-out.
  return (
    <>
      {timing.showSkeleton ? (
        // Every Skeleton is aria-hidden, so without this the card is silent
        // to a screen reader for as long as the request takes. Kept OUTSIDE
        // the `aria-busy` box below on purpose: several screen readers hold
        // back a live region's announcement for as long as its container
        // claims `aria-busy="true"`, which here is the whole loading duration.
        <span role="status" className="sr-only">
          {loadingAnnounced ? 'Loading…' : null}
        </span>
      ) : null}
      <div
        ref={rootRef}
        aria-busy={timing.showSkeleton || isRefreshing || undefined}
        className={cn('refresh-region', timing.className, className)}
      >
        <ArrivalCrossfade updating={isRefreshing} region={rootRef} />
        {isRefreshing ? (
          <span role="status" className="sr-only">
            Refreshing
          </span>
        ) : null}
        {/* The body's own wrapper, as it always was: taking it away now
            would reflow every caller's body. */}
        {timing.showSkeleton ? null : <div>{children}</div>}
        {timing.showSkeleton || timing.skeletonLeaving ? (
          <SkeletonLayer leaving={!timing.showSkeleton}>
            {skeleton ?? DEFAULT_SKELETON}
          </SkeletonLayer>
        ) : null}
      </div>
    </>
  )
}
